import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Reveal, SectionLabel } from './Section';
import { PLACEMENTPILOT_FEATURES, PLACEMENTPILOT_GALLERY, PLACEMENTPILOT_STATS } from '../data/content';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const ROUNDS = ['Technical','Case Study','Behavioral / HR','Final Review','Salary Negotiation'];

export default function PlacementPilot() {
  const [idx, setIdx] = useState(null);

  const next = () => setIdx(i => (i+1) % PLACEMENTPILOT_GALLERY.length);
  const prev = () => setIdx(i => (i-1+PLACEMENTPILOT_GALLERY.length) % PLACEMENTPILOT_GALLERY.length);

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal><SectionLabel>AI Mock Interviews</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <div className="flex items-baseline gap-4 mt-6 flex-wrap">
            <h2 className="font-display text-[54px] sm:text-[80px] lg:text-[110px] leading-[0.95] font-semibold text-[#293891]">PlacementPilot</h2>
            <span className="px-3 py-1 rounded-full bg-[#293891]/10 text-[#293891] text-[12px] font-semibold">by ExcellenC</span>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-[#293891]/75">
            Accelerate your journey from learning to landing your dream career. PlacementPilot is ExcellenC's AI-powered Career Success Platform — practise unlimited voice-enabled mock interviews, get instant AI feedback on every answer, and walk into every real interview with confidence.
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLACEMENTPILOT_STATS.map((s,i) => (
            <Reveal key={s.l} delay={i*0.06}>
              <div className="bg-[#F4F5FB] border border-[#293891]/10 rounded-3xl p-6">
                <div className="font-display text-[54px] leading-none font-semibold text-[#293891]">{s.v}</div>
                <div className="text-[12px] tracking-[0.15em] uppercase mt-3 font-semibold text-[#293891]/80">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 bg-[#F4F5FB] border border-[#293891]/10 rounded-3xl p-8 lg:p-10">
          <div className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#293891]/70">The 5-Round Journey</div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {ROUNDS.map((r,i) => (
              <React.Fragment key={r}>
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#293891] text-white text-[12px] font-semibold flex items-center justify-center">{i+1}</span>
                  <span className="font-display text-[16px] font-semibold text-[#293891]">{r}</span>
                </div>
                {i < ROUNDS.length-1 && <div className="w-8 h-[2px] bg-[#293891]/25"/>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {PLACEMENTPILOT_FEATURES.map((f,i) => (
            <Reveal key={f.title} delay={i*0.05}>
              <div className="card-lift bg-white border border-[#293891]/10 rounded-3xl p-6 h-full">
                <h3 className="font-display text-[19px] font-semibold text-[#293891]">{f.title}</h3>
                <p className="text-[13px] leading-relaxed text-[#293891]/70 mt-3">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16">
            <div className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#293891]/70">Inside the platform</div>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {PLACEMENTPILOT_GALLERY.map((g,i) => (
                <button key={i} onClick={()=>setIdx(i)} className="group text-left">
                  <div className="relative overflow-hidden rounded-2xl border border-[#293891]/10 bg-white">
                    <img src={g.src} alt={g.label} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"/>
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-semibold text-[#293891] rounded-full">Preview {String(i+1).padStart(2,'0')}</div>
                  </div>
                  <div className="mt-3 font-display text-[15px] font-semibold text-[#293891] group-hover:text-[#E63329] transition-colors">{g.label}</div>
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {idx !== null && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={()=>setIdx(null)}>
            <button className="absolute top-6 right-6 text-white/85 hover:text-white" onClick={()=>setIdx(null)}><X size={28}/></button>
            <button className="absolute left-6 text-white/85 hover:text-white" onClick={(e)=>{e.stopPropagation(); prev();}}><ChevronLeft size={40}/></button>
            <button className="absolute right-6 text-white/85 hover:text-white" onClick={(e)=>{e.stopPropagation(); next();}}><ChevronRight size={40}/></button>
            <motion.img key={idx} initial={{opacity:0, scale:0.96}} animate={{opacity:1, scale:1}} exit={{opacity:0}} src={PLACEMENTPILOT_GALLERY[idx].src} alt="" className="max-h-[85vh] max-w-[92vw] rounded-xl shadow-2xl" onClick={e=>e.stopPropagation()}/>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
